import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Seo, { breadcrumbLd } from "../components/Seo";
import { Building2, MapPin, ArrowLeft, Users, Palette } from "lucide-react";

const resolveImg = (url) =>
  url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url;

// v1.47 — magazine-style redesign. Editorial hero image, warm palette,
// bold serif section headers with gold rules, large gallery-style
// artwork cards with hover pan + price overlay.
export default function ArtCenterProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const [center, setCenter] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [membership, setMembership] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/art-centers/${id}`);
        setCenter(data);
      } catch (_e) {
        setNotFound(true);
      }
    })();
  }, [id]);

  const loadMembership = useCallback(async () => {
    if (!user || user.role !== "artist") return;
    try {
      const { data } = await api.get("/memberships/mine");
      setMembership(data);
    } catch (_e) { /* non-blocking */ }
  }, [user]);
  useEffect(() => { loadMembership(); }, [loadMembership]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      await api.post(`/art-centers/${id}/join`);
      toast.success("Request sent — the hub will review it");
      loadMembership();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send request");
    } finally { setJoining(false); }
  };

  if (notFound) {
    return (
      <div className="bg-[#FAF6EE] min-h-screen p-24">
        <p className="font-serif-display text-3xl text-[#7A5C3D]">Art hub not found.</p>
        <Link to="/art-hubs" className="label-eyebrow text-[#B7502A] mt-4 inline-block hover:underline">
          ← All art hubs
        </Link>
      </div>
    );
  }
  if (!center) {
    return <div className="bg-[#FAF6EE] min-h-screen p-24 label-eyebrow text-[#7A5C3D]">Loading…</div>;
  }

  const isArtist = user?.role === "artist";
  const isMemberHere = membership?.active?.center_id === center.id;
  const hasPendingHere = (membership?.pending || []).some((m) => m.center_id === center.id);
  const hasOtherCenter = membership?.active && !isMemberHere;

  let joinCta = null;
  if (isArtist) {
    if (isMemberHere) {
      joinCta = (
        <span data-testid="center-member-badge" className="label-eyebrow bg-[#F4C430] text-[#2A180F] px-4 py-2.5">
          You are a member
        </span>
      );
    } else if (hasPendingHere) {
      joinCta = (
        <span data-testid="center-pending-badge" className="label-eyebrow border border-[#F4C430] text-[#F4C430] px-4 py-2.5">
          Request pending
        </span>
      );
    } else if (!hasOtherCenter) {
      joinCta = (
        <Button
          data-testid="center-join-btn"
          onClick={handleJoin}
          disabled={joining}
          className="rounded-none bg-[#F4C430] hover:bg-[#e0b220] text-[#2A180F] px-8 py-4 h-auto label-eyebrow"
        >
          {joining ? "Sending…" : "Request to join hub"}
        </Button>
      );
    }
  }

  return (
    <div data-testid="art-center-profile-page" className="bg-[#FAF6EE]">
      <Seo
        title={`${center.name} — African Art Hub`}
        description={
          center.bio
            ? `${center.bio.slice(0, 160)}${center.bio.length > 160 ? "…" : ""}`
            : `${center.name}, African art hub${center.country ? ` in ${center.country}` : ""}. Discover its member artists and original works on AfricaArtMasters.`
        }
        path={`/art-hub/${center.id}`}
        jsonLd={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Art Hubs", path: "/art-hubs" },
          { name: center.name, path: `/art-hub/${center.id}` },
        ])}
      />

      {/* --- Full-bleed editorial hero ------------------------------ */}
      <section className="relative overflow-hidden bg-[#2A180F] text-[#F4EFE6]">
        <div className="absolute inset-0">
          {center.avatar_url ? (
            <img
              src={resolveImg(center.avatar_url)}
              alt={`${center.name}, African art hub${center.country ? ` in ${center.country}` : ""}`}
              className="w-full h-full object-cover opacity-45"
            />
          ) : (
            <div
              className="w-full h-full"
              style={{
                background:
                  "radial-gradient(circle at 20% 30%, #C97B3B 0%, transparent 55%), radial-gradient(circle at 80% 70%, #7A3D1F 0%, transparent 60%), linear-gradient(180deg, #3A2418 0%, #2A180F 100%)",
              }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#2A180F] via-[#2A180F]/70 to-[#2A180F]/30" />
        </div>

        <div className="relative px-6 md:px-12 lg:px-24 pt-10 pb-20 md:pb-32 min-h-[70vh] flex flex-col justify-end">
          <Link
            to="/art-hubs"
            className="label-eyebrow text-[#F4C430] hover:text-white transition-colors flex items-center gap-2 mb-auto"
          >
            <ArrowLeft className="w-3 h-3" />
            All Art Hubs
          </Link>

          <div className="mt-10 max-w-5xl">
            <p className="label-eyebrow text-[#F4C430] tracking-[0.3em]">
              — The Collective · {center.country || "Africa"}
            </p>
            <h1
              data-testid="center-name"
              className="font-serif-display text-6xl md:text-8xl lg:text-9xl tracking-tighter leading-[0.88] font-light mt-6"
            >
              {center.name}
            </h1>
            {center.bio && (
              <p className="mt-8 max-w-2xl text-lg md:text-xl leading-relaxed text-[#E8DFCA] font-light">
                {center.bio}
              </p>
            )}
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
              <div className="flex items-center gap-2 label-eyebrow text-[#F4C430]">
                <Users className="w-4 h-4" strokeWidth={1.5} />
                {center.member_count} artist{center.member_count === 1 ? "" : "s"}
              </div>
              <div className="flex items-center gap-2 label-eyebrow text-[#F4C430]">
                <Palette className="w-4 h-4" strokeWidth={1.5} />
                {center.artworks?.length || 0} work{center.artworks?.length === 1 ? "" : "s"}
              </div>
              {center.country && (
                <div className="flex items-center gap-2 label-eyebrow text-[#F4C430]">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                  {center.country}
                </div>
              )}
              {joinCta && <div className="ml-auto">{joinCta}</div>}
            </div>
          </div>
        </div>
      </section>

      {/* --- Mission (pull-quote treatment) ------------------------- */}
      {center.mission_statement && (
        <section
          data-testid="center-mission"
          className="px-6 md:px-12 lg:px-24 py-20 md:py-28 bg-[#F0E4D0]"
        >
          <div className="max-w-4xl mx-auto text-center">
            <p className="label-eyebrow text-[#B7502A] mb-8">— Our Mission</p>
            <p className="font-serif-display text-3xl md:text-5xl lg:text-6xl leading-tight font-light italic text-[#3A2418] tracking-tight">
              &ldquo;{center.mission_statement}&rdquo;
            </p>
            <div className="mx-auto mt-10 h-px w-16 bg-[#C97B3B]" />
          </div>
        </section>
      )}

      {/* --- About the hub ------------------------------------------ */}
      {center.about && (
        <section
          data-testid="center-about-section"
          className="px-6 md:px-12 lg:px-24 py-20 md:py-28"
        >
          <div className="grid grid-cols-12 gap-y-8 gap-x-4 lg:gap-16">
            <div className="col-span-12 lg:col-span-4">
              <p className="label-eyebrow text-[#B7502A]">— About the hub</p>
              <h2 className="font-serif-display text-4xl md:text-5xl tracking-tighter font-light mt-3 leading-[0.95]">
                Inside <em className="text-[#B7502A]">{center.name}</em>
              </h2>
              <div className="mt-6 h-px w-16 bg-[#F4C430]" />
            </div>
            <div className="col-span-12 lg:col-span-8">
              <p
                data-testid="center-about-body"
                className="text-lg md:text-xl text-[#3A2418] leading-relaxed font-light whitespace-pre-line first-letter:font-serif-display first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:text-[#B7502A]"
              >
                {center.about}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* --- Member artists ----------------------------------------- */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28 bg-[#3A2418] text-[#F4EFE6]">
        <SectionHeader
          eyebrow="The Artists"
          headline="Member artists"
          tone="dark"
          count={center.artists?.length || 0}
        />
        {center.artists?.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 lg:gap-10">
            {center.artists.map((a) => (
              <Link
                to={`/artist/${a.id}`}
                key={a.id}
                data-testid={`center-artist-${a.id}`}
                className="group block"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#5A3B2A]">
                  <img
                    src={
                      resolveImg(a.avatar_url) ||
                      "https://static.prod-images.emergentagent.com/jobs/2cf555d8-2b2e-4a65-8a76-f17be07c4f4a/images/d11b06ed5c6c920763ec229eb9d9f1c7ad885e95f977d086b28da211c4d8e0ef.png"
                    }
                    alt={`${a.name}, member artist at ${center.name}`}
                    className="w-full h-full object-cover transition-all duration-[1200ms] ease-out group-hover:scale-105"
                    style={{ filter: "sepia(0.15) saturate(0.9)" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2A180F]/85 via-transparent to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <h3 className="font-serif-display text-xl md:text-2xl leading-tight text-[#F4EFE6]">
                      {a.name}
                    </h3>
                    <div className="mt-2 h-px w-8 bg-[#F4C430] transition-all duration-500 group-hover:w-20" />
                    <p className="text-xs text-[#E8DFCA]/80 mt-2 font-light tracking-wide">
                      {a.country || "Africa"}
                      <span className="mx-2 text-[#F4C430]">·</span>
                      {a.artwork_count} work{a.artwork_count === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="font-serif-display text-2xl text-[#E8DFCA]/70">
            No published member artists yet.
          </p>
        )}
      </section>

      {/* --- Works from the hub ------------------------------------- */}
      <section className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <SectionHeader
          eyebrow={`Presented by ${center.name}`}
          headline="Works from the hub"
          tone="light"
          count={center.artworks?.length || 0}
        />
        {center.artworks?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
            {center.artworks.map((art) => (
              <HubArtworkCard key={art.id} art={art} />
            ))}
          </div>
        ) : (
          <p className="font-serif-display text-2xl text-[#7A5C3D]">
            No works on view yet.
          </p>
        )}
      </section>

      {/* --- Studio & exhibition gallery ---------------------------- */}
      {center.center_gallery?.length > 0 && (
        <section
          data-testid="center-gallery-section"
          className="px-6 md:px-12 lg:px-24 py-20 md:py-28 bg-[#F0E4D0]"
        >
          <SectionHeader
            eyebrow="Inside the hub"
            headline="Studio & exhibitions"
            tone="light"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {center.center_gallery.map((url, i) => (
              <div
                key={url}
                className={`relative overflow-hidden bg-[#E8DFCA] group ${
                  i % 5 === 0 ? "col-span-2 row-span-2 aspect-square" : "aspect-square"
                }`}
              >
                <img
                  src={resolveImg(url)}
                  alt={`${center.name} studio and exhibition photo ${i + 1}`}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* --- News / stories ----------------------------------------- */}
      {center.posts?.length > 0 && (
        <section
          data-testid="center-stories-section"
          className="px-6 md:px-12 lg:px-24 py-20 md:py-28"
        >
          <SectionHeader
            eyebrow={`News from ${center.name}`}
            headline="Stories"
            tone="light"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {center.posts.map((p) => (
              <article
                key={p.id}
                data-testid={`center-story-${p.id}`}
                className="group bg-white border border-[#E5D8BE] hover:border-[#B7502A] transition-colors overflow-hidden"
              >
                {p.image_url && (
                  <div className="aspect-[5/3] bg-[#F0E4D0] overflow-hidden">
                    <img
                      src={resolveImg(p.image_url)}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-7 md:p-9">
                  <p className="label-eyebrow text-[#B7502A]">
                    {new Date(p.created_at).toLocaleDateString(undefined, {
                      year: "numeric", month: "long", day: "numeric",
                    })}
                  </p>
                  <h3 className="font-serif-display text-2xl md:text-3xl mt-3 leading-tight text-[#3A2418]">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#5A3B2A] mt-4 leading-relaxed whitespace-pre-line font-light">
                    {p.body}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* --- Downloads ---------------------------------------------- */}
      {center.center_documents?.length > 0 && (
        <section
          data-testid="center-documents-section"
          className="px-6 md:px-12 lg:px-24 py-20 md:py-28 bg-[#F0E4D0]"
        >
          <SectionHeader
            eyebrow="Downloads"
            headline="Catalogues & brochures"
            tone="light"
          />
          <div className="bg-white border border-[#E5D8BE] max-w-3xl">
            {center.center_documents.map((d) => (
              <a
                key={d.id}
                href={resolveImg(d.url)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`center-public-doc-${d.id}`}
                className="flex items-center justify-between gap-3 px-7 py-5 border-b border-[#E5D8BE] last:border-0 hover:bg-[#F4C430]/15 transition-colors group"
              >
                <span className="font-serif-display text-xl text-[#3A2418]">{d.title}</span>
                <span className="label-eyebrow text-[#B7502A] group-hover:text-[#3A2418] transition-colors">
                  Download PDF ↓
                </span>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Editorial section header                                            */
/* ------------------------------------------------------------------ */
function SectionHeader({ eyebrow, headline, tone = "light", count }) {
  const isDark = tone === "dark";
  return (
    <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
      <div>
        <p className={`label-eyebrow ${isDark ? "text-[#F4C430]" : "text-[#B7502A]"}`}>
          — {eyebrow}
        </p>
        <h2
          className={`font-serif-display text-4xl md:text-6xl tracking-tighter font-light mt-3 leading-[0.95] ${
            isDark ? "text-[#F4EFE6]" : "text-[#3A2418]"
          }`}
        >
          {headline}
        </h2>
        <div className={`mt-5 h-px w-20 ${isDark ? "bg-[#F4C430]" : "bg-[#F4C430]"}`} />
      </div>
      {typeof count === "number" && (
        <p
          className={`font-mono-aam text-xs uppercase tracking-widest ${
            isDark ? "text-[#F4C430]/80" : "text-[#7A5C3D]"
          }`}
        >
          {count} on view
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Gallery-quality artwork card                                        */
/* ------------------------------------------------------------------ */
function HubArtworkCard({ art }) {
  const img = resolveImg(art.image_url);
  const price = art.price
    ? new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: art.currency || "USD",
        maximumFractionDigits: 0,
      }).format(art.price)
    : null;
  return (
    <Link
      to={`/artwork/${art.id}`}
      data-testid={`hub-artwork-${art.id}`}
      className="group block"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#E8DFCA]">
        <img
          src={img}
          alt={art.title}
          className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2A180F]/85 via-[#2A180F]/10 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
        {price && (
          <p className="absolute top-4 right-4 font-mono-aam text-[11px] tracking-widest bg-[#F4C430] text-[#2A180F] px-2.5 py-1">
            {price}
          </p>
        )}
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-7">
          <p className="label-eyebrow text-[#F4C430]">
            {art.medium || "Original"}
          </p>
          <h3 className="font-serif-display text-2xl md:text-3xl mt-2 leading-tight text-[#F4EFE6]">
            {art.title}
          </h3>
          <div className="mt-3 h-px w-10 bg-[#F4C430] transition-all duration-500 group-hover:w-24" />
          <p className="mt-3 text-sm text-[#E8DFCA] font-light">
            {art.artist_name}
            {art.dimensions ? (
              <span className="ml-2 text-[#E8DFCA]/60">· {art.dimensions}</span>
            ) : null}
          </p>
        </div>
      </div>
    </Link>
  );
}
